import React from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import ButtonItem from "./ButtonItem";

/**
 * Pantalla de bloqueo que se muestra en lugar del formulario cuando un documento
 * (pedido, cotizacion, embarque) tiene renglones que apuntan a insumos que ya no
 * estan en el catalogo. Sin el insumo no se puede reconstruir arrayImpuestos del
 * renglon, asi que la edicion no puede continuar.
 *
 * El numero de linea no es decoracion: es la posicion real del renglon en el
 * documento, y es lo que el usuario necesita para ubicarlo.
 *
 * @param {string}   documento  - "pedido" | "cotizacion" | "embarque" (para los textos)
 * @param {Array}    lineas     - [{ linea, clave, codigo, descripcion, motivoTexto }]
 * @param {Function} onRegresar - regresa a la pantalla anterior
 */

// El color del estado, no el de la marca: aqui el cian de Biwo leeria como "todo bien".
const T = {
    alerta: "#A32015",
    alertaFondo: "#FCF2F1",
    alertaBorde: "#F0DAD7",
    borde: "#E4E7EC",
    texto: "#3D4451",
    mudo: "#79818F",
    separador: "#F0F2F5",
};

const ARTICULO = {
    pedido: "este pedido",
    cotizacion: "esta cotizacion",
    embarque: "este embarque",
};

const AvisoInsumosNoDisponibles = ({ documento = "pedido", lineas = [], onRegresar }) => {
    const total = lineas.length;

    return (
        <div style={{ padding: "24px 16px" }}>
            <div
                style={{
                    maxWidth: 760,
                    margin: "0 auto",
                    backgroundColor: "white",
                    border: `1px solid ${T.borde}`,
                    borderRadius: 8,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        gap: 14,
                        padding: "18px 22px",
                        backgroundColor: T.alertaFondo,
                        borderBottom: `1px solid ${T.alertaBorde}`,
                        borderLeft: `4px solid ${T.alerta}`,
                    }}
                >
                    <ExclamationCircleFilled
                        style={{ color: T.alerta, fontSize: 20, marginTop: 2, flexShrink: 0 }}
                    />
                    <div>
                        <div
                            style={{
                                fontSize: 17,
                                fontWeight: 600,
                                color: T.alerta,
                                marginBottom: 6,
                            }}
                        >
                            No se puede editar {ARTICULO[documento] || "este documento"}
                        </div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: T.texto }}>
                            {total === 1
                                ? "Un renglon apunta a un insumo que ya no esta en el catalogo."
                                : `${total} renglones apuntan a insumos que ya no estan en el catalogo.`}{" "}
                            Sin el insumo no se pueden recalcular los impuestos del documento.
                        </div>
                    </div>
                </div>

                <div style={{ padding: "4px 22px" }}>
                    {lineas.map((item, i) => (
                        <div
                            key={`${item.linea}-${item.clave || item.codigo || i}`}
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "baseline",
                                gap: "4px 14px",
                                padding: "13px 0",
                                borderTop: i === 0 ? "none" : `1px solid ${T.separador}`,
                            }}
                        >
                            <span
                                style={{
                                    flexShrink: 0,
                                    minWidth: 62,
                                    fontSize: 12,
                                    color: T.mudo,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                Linea{" "}
                                <span style={{ fontSize: 14, fontWeight: 700, color: T.texto }}>
                                    {item.linea}
                                </span>
                            </span>

                            <span style={{ fontSize: 13, color: T.texto, minWidth: 0 }}>
                                {(item.clave || item.codigo) && (
                                    <span style={{ fontWeight: 600 }}>
                                        {item.clave || item.codigo}{" "}
                                    </span>
                                )}
                                {item.descripcion || "Insumo sin datos"}
                            </span>

                            <span
                                style={{
                                    marginLeft: "auto",
                                    flexShrink: 0,
                                    fontSize: 12,
                                    color: T.alerta,
                                    backgroundColor: T.alertaFondo,
                                    border: `1px solid ${T.alertaBorde}`,
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                }}
                            >
                                {item.motivoTexto}
                            </span>
                        </div>
                    ))}
                </div>

                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "16px 22px",
                        borderTop: `1px solid ${T.borde}`,
                        backgroundColor: "#FCFCFD",
                    }}
                >
                    <span style={{ fontSize: 13, color: T.mudo, maxWidth: 480, lineHeight: 1.5 }}>
                        Habilite el insumo en el catalogo, o cancele el documento y levante uno nuevo.
                    </span>
                    <ButtonItem type="primary" onClick={onRegresar}>
                        Regresar
                    </ButtonItem>
                </div>
            </div>
        </div>
    );
};

export default AvisoInsumosNoDisponibles;
